import { useRef, useState } from "react";
import { formatBytes } from "../lib/format";
import { backdropClose } from "../lib/backdrop";

/**
 * Tick some files out of a list, then act on them.
 *
 * Two questions in this app have exactly this shape - which of a delivery to
 * proof on arrival, and which of the parked files to proof now - and they
 * should not look or behave like two different things. Only the words change.
 */
export function FileChoiceDialog({
  title, lede, items, initial = () => true, tagOn, tagOff,
  confirmLabel, summary, onCancel, onConfirm,
}: {
  title: string;
  lede: React.ReactNode;
  items: { name: string; size?: number; note?: string }[];
  /** Which rows start ticked. Everything, unless a caller says otherwise. */
  initial?: (name: string) => boolean;
  tagOn: string;
  tagOff: string;
  confirmLabel: (picked: number) => string;
  summary: (picked: number, rest: number) => string;
  onCancel: () => void;
  onConfirm: (picked: Set<string>) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(items.filter((i) => initial(i.name)).map((i) => i.name)),
  );
  const anchor = useRef<number | null>(null);

  /** Click sets one and anchors, Shift extends the run - the same as the file
   *  list this stands in front of. */
  const tick = (i: number, e: React.MouseEvent<HTMLInputElement>) => {
    const name = items[i].name;
    const want = !picked.has(name);
    const next = new Set(picked);
    if (e.shiftKey && anchor.current !== null) {
      const [lo, hi] = anchor.current < i ? [anchor.current, i] : [i, anchor.current];
      for (let k = lo; k <= hi; k++) {
        if (want) next.add(items[k].name); else next.delete(items[k].name);
      }
    } else {
      if (want) next.add(name); else next.delete(name);
      anchor.current = i;
    }
    setPicked(next);
  };

  const n = picked.size;

  return (
    <div className="modal-overlay" {...backdropClose(onCancel)}>
      <div className="modal-card fpick" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p className="fpick-lede">{lede}</p>

        <div className="fpick-bar">
          <span>{items.length} file{items.length === 1 ? "" : "s"}</span>
          <span className="fpick-bar-actions">
            <button type="button" className="btn ghost sm"
              onClick={() => { anchor.current = null; setPicked(new Set(items.map((i) => i.name))); }}>
              All
            </button>
            <button type="button" className="btn ghost sm"
              onClick={() => { anchor.current = null; setPicked(new Set()); }}>
              None
            </button>
          </span>
        </div>

        <div className="fpick-list">
          {items.map((it, i) => (
            <label key={it.name} className={`fpick-row${picked.has(it.name) ? " on" : ""}`}>
              <input type="checkbox" checked={picked.has(it.name)}
                onChange={() => { /* driven by onClick, which carries the modifiers */ }}
                onClick={(e) => tick(i, e)} />
              <span className="fpick-name mono">{it.name}</span>
              <span className="fpick-note">{it.note ?? (it.size != null ? formatBytes(it.size) : "")}</span>
              <span className={`fpick-tag${picked.has(it.name) ? " on" : ""}`}>
                {picked.has(it.name) ? tagOn : tagOff}
              </span>
            </label>
          ))}
        </div>

        <p className="fpick-foot">{summary(n, items.length - n)}</p>

        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn primary" disabled={n === 0 && items.length > 0 && confirmLabel(0) === ""}
            onClick={() => onConfirm(picked)}>
            {confirmLabel(n)}
          </button>
        </div>
      </div>
    </div>
  );
}
